import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, Inject, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { AutoCallBackService } from '../../../../services/auto-call-back.service';
import { AiAgentResponse } from '../../../../models/responses/ai-agent-response';
import { AutoCallBackSettingsResponse } from '../../../../models/responses/auto-call-back-settings-response';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AiConfigurationPageEventsService } from '../../../../events/ai-configuration-page-events.service';
import { MISSED_CALL_ICON } from '../../../../app.component';
import { FUNCTIONAL_ERROR, getErrorType, handleErrors } from '../../../../utilities/error-handler-utils';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { UnitOfTime } from '../../../../models/enums/unit-of-time';
import { MinutesTimeUnitMappingComponent } from '../../../minutes-time-unit-mapping/minutes-time-unit-mapping.component';

type CallBackScheduledUnitPair = {
  callBackScheduledat: number;
  unit: UnitOfTime;
};
@Component({
  selector: 'app-missed-call-action-settings',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    NgFor,
    MatIconModule,
    MatDialogModule,
    AsyncPipe,
    MatPaginatorModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    MatSelectModule,
    NgClass,
    NgFor,
    NgIf,
    MinutesTimeUnitMappingComponent
  ],
  templateUrl: './auto-call-back-action-settings.component.html',
  styleUrl: './auto-call-back-action-settings.component.scss'
})
export class AutoCallBackSettingsComponent {
  missedCallIcon = MISSED_CALL_ICON;
  errorMessage: string | undefined;
  missedCallSettingsForm: FormGroup;
  scheduledAt:number;
  appMinuteTimeUnitMappingLabel = `When would you like to call back the user who've missed your call.`;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  timeUnits = Object.values(UnitOfTime);
  private timeUnitToMultiplicationFactor = new Map<UnitOfTime, number>([
    [UnitOfTime.MINUTES, 1],
    [UnitOfTime.HOURS, 60],
    [UnitOfTime.DAYS, 60 * 24]
  ]);
  isLoading = false;
  constructor(
    private formBuilder: FormBuilder,
    private autoCallBackService: AutoCallBackService,
    public dialogRef: MatDialogRef<AutoCallBackSettingsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { aiAgent: AiAgentResponse, missedCallSettings: AutoCallBackSettingsResponse },
    private snackBar: MatSnackBar,
    private aiConfigurationPageEventService: AiConfigurationPageEventsService
  ) {
    this.scheduledAt = data.missedCallSettings?.callbackScheduledAt || 0;
    this.missedCallSettingsForm = this.initForm();
  }
  ngOnInit() {

  }

  initForm() {
    const formatedScheduledAt = this.getTimeUnit(this.data.missedCallSettings?.callbackScheduledAt)

    return this.formBuilder.group({
      callbackScheduledAt: [formatedScheduledAt.callBackScheduledat, Validators.required],
      callbackMaxAttempts: [this.data.missedCallSettings?.callbackMaxAttempts || undefined, Validators.required],
      timeUnit: [formatedScheduledAt.unit, Validators.required]
    })
  }

  onMinutesValueChange(newValue:number){
    this.scheduledAt = newValue
  }

  onSubmit() {
    if (this.missedCallSettingsForm.valid) {
      const values = this.missedCallSettingsForm.value;
      this.isLoading = true;

      console.log(this.scheduledAt)

      const request: AutoCallBackSettingsResponse = {
        callbackMaxAttempts: values['callbackMaxAttempts'],
        callbackScheduledAt: this.scheduledAt,
        id: this.data.missedCallSettings?.id,
        outBoundSettingId: this.data.aiAgent.outBoundSettings!.id
      }
      if (this.data.missedCallSettings) {
        this.updateAutoCallbackSettings(request);
      } else {
        this.createAutoCallbackSettings(request);
      }
    }
  }

  onDelete(id: number) {
    this.isLoading = true;
    this.autoCallBackService.deleteMissedCallActionSettings(id).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.dialogRef.close({ deleted: true });
        this.snackBar.open('Auto Call Back Settings deleted successfully', 'Close', { duration: 5000 });
      },
      error: (err) => this.handleErrorInternal(err)
    })
  }

  private createAutoCallbackSettings(request: AutoCallBackSettingsResponse) {
    this.autoCallBackService.createMissedCallActionSettings(request).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.aiConfigurationPageEventService.onMissedCallBackActionsSettingsAddedEvent.emit(response);
        this.dialogRef.close({ missedCallSettings: response });
        this.snackBar.open('Auto Call Back Settings added successfully', 'Close', { duration: 5000 });
      },
      error: (err) => this.handleErrorInternal(err)
    })
  }
  private updateAutoCallbackSettings(request: AutoCallBackSettingsResponse) {
    this.autoCallBackService.updateMissedCallActionSettings(request).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        this.aiConfigurationPageEventService.onMissedCallBackActionsSettingsAddedEvent.emit(response);
        this.dialogRef.close({ missedCallSettings: response });
        this.snackBar.open('Auto Call Back Settings updated successfully', 'Close', { duration: 5000 });
      },
      error: (err: any) => this.handleErrorInternal(err)
    })
  }

  private handleErrorInternal(err: any) {
    this.errorMessage = handleErrors(err);
    this.isLoading = false;
  }

  private getTimeUnit(callBackScheduedAt: number): CallBackScheduledUnitPair {
    if(!callBackScheduedAt){
      return { callBackScheduledat: 0, unit: UnitOfTime.MINUTES }
    }

    const sortedMap = [...this.timeUnitToMultiplicationFactor.entries()].sort((a: [UnitOfTime, number], b: [UnitOfTime, number]) => {
      return b[1] - a[1]
    });


    for (const multiplifactorPair of sortedMap) {
      const callBackScheduledAtInTimeUnit = callBackScheduedAt / multiplifactorPair[1];
      if (callBackScheduledAtInTimeUnit > 1) {
        console.log(callBackScheduledAtInTimeUnit)
        return { callBackScheduledat: parseFloat(callBackScheduledAtInTimeUnit.toFixed(2)), unit: multiplifactorPair[0] }
      }
    }

    return { callBackScheduledat: callBackScheduedAt, unit: UnitOfTime.MINUTES }
  }
}
